import { supabase } from './supabase'

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000'

// ─── In-memory cache ──────────────────────────────────────────────────────────
// Keyed by endpoint URL. Survives client-side navigation (module stays loaded)
// but resets on full page reload. TTLs are in milliseconds.
const _cache = new Map()

function _cacheGet(key) {
  const entry = _cache.get(key)
  if (!entry) return null
  if (Date.now() > entry.expiresAt) { _cache.delete(key); return null }
  return entry.data
}

function _cacheSet(key, data, ttlMs) {
  _cache.set(key, { data, expiresAt: Date.now() + ttlMs })
}

// Deletes all cache entries whose key contains any of the given substrings.
// Called automatically after mutations that would stale related GET responses.
export function invalidateCache(...patterns) {
  for (const key of _cache.keys()) {
    if (patterns.some(p => key.includes(p))) _cache.delete(key)
  }
}

async function cachedRequest(endpoint, ttlMs) {
  const hit = _cacheGet(endpoint)
  if (hit !== null) return hit
  const data = await apiRequest(endpoint)
  _cacheSet(endpoint, data, ttlMs)
  return data
}
// ─────────────────────────────────────────────────────────────────────────────

async function getAuthHeaders() {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.access_token) {
    throw new Error('No active session')
  }
  return {
    'Authorization': `Bearer ${session.access_token}`,
    'Content-Type': 'application/json',
  }
}

export async function apiRequest(endpoint, options = {}) {
  try {
    const headers = await getAuthHeaders()
    const url = `${API_BASE_URL}${endpoint}`

    const response = await fetch(url, {
      ...options,
      headers: {
        ...headers,
        ...options.headers,
      },
    })

    if (!response.ok) {
      let errorMessage = `HTTP ${response.status}`
      let errorCode
      try {
        const errorData = await response.json()
        errorMessage = errorData.message || errorData.code || errorMessage
        errorCode = errorData.code
        if (errorData.errors && Array.isArray(errorData.errors)) {
          errorMessage += ': ' + errorData.errors.map(e => e.message || e.field).join(', ')
        }
      } catch (e) {
        // If response is not JSON, use status text
        errorMessage = response.statusText || errorMessage
      }
      const httpError = new Error(errorMessage)
      if (errorCode) httpError.code = errorCode
      throw httpError
    }

    // 204 No Content has an empty body — response.json() would throw
    if (response.status === 204) return null

    return response.json()
  } catch (error) {
    // Re-throw if it's already our formatted error
    if (error.code || (error.message && error.message.startsWith('HTTP'))) {
      throw error
    }
    // Otherwise, wrap network/other errors
    throw new Error(error.message || 'Network request failed')
  }
}

export async function hasSession() {
  const { data: { session } } = await supabase.auth.getSession()
  return Boolean(session?.access_token)
}

/**
 * Request without an Authorization header, for the /v1/public/* endpoints.
 * Deliberately separate from apiRequest: an authed call that loses its token
 * should fail loudly rather than silently downgrade to anonymous.
 */
export async function publicRequest(endpoint, options = {}) {
  const url = `${API_BASE_URL}${endpoint}`
  // Strip any caller-supplied Authorization header to prevent accidental auth leakage
  const { Authorization, ...safeHeaders } = options.headers || {}
  const response = await fetch(url, {
    ...options,
    headers: { 'Content-Type': 'application/json', ...safeHeaders },
  })

  if (!response.ok) {
    let errorMessage = `HTTP ${response.status}`
    if (response.status === 429) {
      errorMessage = 'Too many attempts, please wait a moment and try again.'
    } else {
      try {
        const errorData = await response.json()
        errorMessage = errorData.message || errorData.code || errorMessage
      } catch (e) {
        errorMessage = response.statusText || errorMessage
      }
    }
    const error = new Error(errorMessage)
    error.status = response.status
    throw error
  }

  return response.json()
}

// API methods
export const api = {
  // Get current user with roles
  async getMe() {
    return cachedRequest('/api/v1/auth/me', 60_000)
  },

  // Get current user profile
  async getUser() {
    return cachedRequest('/api/v1/users', 60_000)
  },

  // Get user roles with parish info
  async getUserRoles(userId) {
    return apiRequest(`/api/v1/users/${userId}/roles`)
  },

  // Update user profile (firstName, lastName, name)
  async updateUser(userId, data) {
    const result = await apiRequest(`/api/v1/users/${userId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
    invalidateCache('/api/v1/users')
    invalidateCache('/api/v1/auth/me')
    return result
  },

  // Delete user account
  async deleteAccount(userId) {
    return apiRequest(`/api/v1/users/${userId}`, {
      method: 'DELETE',
    })
  },

  // Update user parish association
  async updateUserParish(userId, parishId, isPrimary = false) {
    const result = await apiRequest(`/api/v1/users/${userId}/parish`, {
      method: 'PATCH',
      body: JSON.stringify({ parishId, isPrimary }),
    })
    invalidateCache('/api/v1/users')
    return result
  },

  // Get parish by ID
  async getParish(parishId) {
    return cachedRequest(`/api/v1/parishes/${parishId}`, 120_000)
  },

  // Search parishes
  async searchParishes({ page = 1, limit = 20, search, state } = {}) {
    const params = new URLSearchParams()
    if (page) params.append('page', page)
    if (limit) params.append('limit', limit)
    if (search) params.append('search', search)
    if (state) params.append('state', state)

    const query = params.toString()
    return cachedRequest(`/api/v1/parishes${query ? `?${query}` : ''}`, 120_000)
  },

  // Find nearest parishes
  async findNearestParishes({ address, latitude, longitude, limit = 10, maxDistance } = {}) {
    return apiRequest('/api/v1/parishes/nearest', {
      method: 'POST',
      body: JSON.stringify({ address, latitude, longitude, limit, maxDistance }),
    })
  },

  // Create parish (super-admin only)
  async createParish(data) {
    const result = await apiRequest('/api/v1/parishes', {
      method: 'POST',
      body: JSON.stringify(data),
    })
    invalidateCache('/api/v1/parishes')
    return result
  },

  // Update parish
  async updateParish(parishId, data) {
    const result = await apiRequest(`/api/v1/parishes/${parishId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
    invalidateCache('/api/v1/parishes')
    return result
  },

  // Stripe Connect: check if parish is ready to accept donations (public, no auth needed)
  async stripeIsReady(parishId) {
    return cachedRequest(`/api/v1/parishes/${parishId}/stripe/is-ready`, 120_000)
  },

  // Stripe Connect: get full onboarding status for a parish (requires auth)
  async stripeStatus(parishId) {
    return apiRequest(`/api/v1/parishes/${parishId}/stripe/status`)
  },

  // Stripe Connect: create onboarding link (redirects to Stripe). Body: { returnUrl, refreshUrl }
  async stripeOnboard(parishId, { returnUrl, refreshUrl }) {
    return apiRequest(`/api/v1/parishes/${parishId}/stripe/onboard`, {
      method: 'POST',
      body: JSON.stringify({ returnUrl, refreshUrl }),
    })
  },

  // Stripe Connect: create Login Link to Express Dashboard (edit bank/business details, view payouts). No body.
  async stripeUpdateLink(parishId) {
    return apiRequest(`/api/v1/parishes/${parishId}/stripe/update-link`, {
      method: 'POST',
    })
  },

  // Stripe Connect: trigger instant payout of the available balance.
  async stripeInstantPayout(parishId, amountCents) {
    return apiRequest(`/api/v1/parishes/${parishId}/stripe/instant-payout`, {
      method: 'POST',
      body: JSON.stringify({ amountCents }),
    })
  },

  // Create Stripe Checkout Session for one-time donation
  // Body: { parishId, amount (in cents), returnUrl, applicationFeeAmount (optional) }
  async createCheckoutSession({ parishId, amount, returnUrl, applicationFeeAmount }) {
    return apiRequest('/api/v1/parishes/checkout-session', {
      method: 'POST',
      body: JSON.stringify({ parishId, amount, returnUrl, applicationFeeAmount }),
    })
  },

  // Verify checkout session and log donation
  // Query: { session_id }
  async verifyCheckoutSession(sessionId) {
    return apiRequest(`/api/v1/parishes/checkout-session/verify?session_id=${encodeURIComponent(sessionId)}`, {
      method: 'POST',
    })
  },

  // Year-end giving statements
  async getGivingStatementYears() {
    return apiRequest('/api/v1/me/giving-statements')
  },

  // Returns the statement PDF as a Blob (apiRequest only parses JSON)
  async downloadGivingStatement(year) {
    const headers = await getAuthHeaders()
    const response = await fetch(`${API_BASE_URL}/api/v1/me/giving-statements/${year}`, {
      headers: { Authorization: headers.Authorization },
    })
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`)
    }
    return response.blob()
  },

  // Get user donations list
  // Query: { page, limit, donationType, parishId, startDate, endDate }
  async getUserDonations(userId, { page = 1, limit = 20, donationType, parishId, startDate, endDate } = {}) {
    const params = new URLSearchParams()
    if (page) params.append('page', page)
    if (limit) params.append('limit', limit)
    if (donationType) params.append('donationType', donationType)
    if (parishId) params.append('parishId', parishId)
    if (startDate) params.append('startDate', startDate)
    if (endDate) params.append('endDate', endDate)
    return cachedRequest(`/api/v1/users/${userId}/donations${params.toString() ? `?${params.toString()}` : ''}`, 30_000)
  },

  // Refund requests (uncached: status changes after an admin review)
  async createRefundRequest(parishId, donationId, { reason, amountCents } = {}) {
    const body = { reason }
    if (amountCents != null) body.amountCents = amountCents
    return apiRequest(`/api/v1/parishes/${parishId}/donations/${donationId}/refund-requests`, {
      method: 'POST',
      body: JSON.stringify(body),
    })
  },

  async listParishRefundRequests(parishId) {
    return apiRequest(`/api/v1/parishes/${parishId}/refund-requests`)
  },

  // Get parish donation dashboard (comprehensive stats)
  // Options: { startDate, endDate, donationType, customFieldId, customFieldValue }
  // - ISO 8601 date strings, donationType: 'one-time' | 'recurring' | 'roundup'
  // customFieldId/customFieldValue filter the recent donations list by a donor's
  // answer; they MUST be passed identically to getParishDonations, or the CSV
  // export disagrees with the list the admin is looking at.
  async getParishDonationDashboard(parishId, { startDate, endDate, donationType, customFieldId, customFieldValue } = {}) {
    const params = new URLSearchParams()
    if (startDate) params.append('startDate', startDate)
    if (endDate) params.append('endDate', endDate)
    if (donationType) params.append('donationType', donationType)
    if (customFieldId && customFieldValue) {
      params.append('customFieldId', customFieldId)
      params.append('customFieldValue', customFieldValue)
    }
    return cachedRequest(`/api/v1/parishes/${parishId}/donation-dashboard${params.toString() ? `?${params.toString()}` : ''}`, 60_000)
  },

  // Get parish donations list
  // Query: { page, limit, startDate, endDate, donationType, customFieldId, customFieldValue }
  async getParishDonations(parishId, { page = 1, limit = 20, startDate, endDate, donationType, customFieldId, customFieldValue } = {}) {
    const params = new URLSearchParams()
    if (page) params.append('page', page)
    if (limit) params.append('limit', limit)
    if (startDate) params.append('startDate', startDate)
    if (endDate) params.append('endDate', endDate)
    if (donationType) params.append('donationType', donationType)
    if (customFieldId && customFieldValue) {
      params.append('customFieldId', customFieldId)
      params.append('customFieldValue', customFieldValue)
    }
    return cachedRequest(`/api/v1/parishes/${parishId}/donations${params.toString() ? `?${params.toString()}` : ''}`, 60_000)
  },

  // Upload parish image
  async uploadParishImage(parishId, imageFile) {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.access_token) {
        throw new Error('No active session')
      }

      const formData = new FormData()
      formData.append('image', imageFile)

      const url = `${API_BASE_URL}/api/v1/parishes/${parishId}/image`

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          // Don't set Content-Type - browser will set it with boundary for FormData
        },
        body: formData,
      })

      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}`
        try {
          const errorData = await response.json()
          errorMessage = errorData.message || errorData.code || errorMessage
          if (errorData.errors && Array.isArray(errorData.errors)) {
            errorMessage += ': ' + errorData.errors.map(e => e.message || e.field).join(', ')
          }
        } catch (e) {
          errorMessage = response.statusText || errorMessage
        }
        throw new Error(errorMessage)
      }

      const data = await response.json()
      invalidateCache('/api/v1/parishes')
      return data
    } catch (error) {
      if (error.message && error.message.startsWith('HTTP')) {
        throw error
      }
      throw new Error(error.message || 'Network request failed')
    }
  },

  // Admin: org verification (super-admin only)
  async getOrgVerification(parishId) {
    return apiRequest(`/api/v1/admin/parishes/${parishId}/verification`)
  },

  async updateOrgVerification(parishId, body) {
    const result = await apiRequest(`/api/v1/admin/parishes/${parishId}/verification`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    })
    invalidateCache('/api/v1/parishes')
    return result
  },

  // kind: 'irs_ein_letter' | 'diocesan_inclusion_letter' | 'other'
  async uploadVerificationDocument(parishId, file, kind) {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.access_token) {
        throw new Error('No active session')
      }

      const formData = new FormData()
      formData.append('file', file)
      formData.append('kind', kind)

      const response = await fetch(`${API_BASE_URL}/api/v1/admin/parishes/${parishId}/verification/documents`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          // Don't set Content-Type - browser will set it with boundary for FormData
        },
        body: formData,
      })

      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}`
        try {
          const errorData = await response.json()
          errorMessage = errorData.message || errorData.code || errorMessage
          if (Array.isArray(errorMessage)) errorMessage = errorMessage.join(', ')
        } catch (e) {
          errorMessage = response.statusText || errorMessage
        }
        throw new Error(errorMessage)
      }

      const data = await response.json()
      invalidateCache('/api/v1/parishes')
      return data
    } catch (error) {
      if (error.message && error.message.startsWith('HTTP')) {
        throw error
      }
      throw new Error(error.message || 'Network request failed')
    }
  },

  async getVerificationDocumentUrl(parishId, path) {
    return apiRequest(
      `/api/v1/admin/parishes/${parishId}/verification/documents/url?path=${encodeURIComponent(path)}`,
    )
  },

  async deleteVerificationDocument(parishId, path) {
    const result = await apiRequest(
      `/api/v1/admin/parishes/${parishId}/verification/documents?path=${encodeURIComponent(path)}`,
      { method: 'DELETE' },
    )
    invalidateCache('/api/v1/parishes')
    return result
  },

  // Admin: Get all users (super-admin only)
  async getAllUsers() {
    return cachedRequest('/api/v1/users/all', 30_000)
  },

  // Admin: Get user by ID
  async getUserById(userId) {
    return apiRequest(`/api/v1/users/${userId}`)
  },

  // Admin: Update user roles
  // roles should be an array of { roleName: string, parishId: string | null }
  async updateUserRoles(userId, roles) {
    const result = await apiRequest(`/api/v1/users/${userId}/roles`, {
      method: 'PATCH',
      body: JSON.stringify({ roles }),
    })
    invalidateCache('/api/v1/users/all', '/api/v1/auth/me')
    return result
  },

  // ============================================================================
  // Parish Invites & Member Roles
  // ============================================================================

  async listParishInvites(parishId) {
    return apiRequest(`/api/v1/parishes/${parishId}/invites`)
  },

  async createParishInvite(parishId, { email, role }) {
    const result = await apiRequest(`/api/v1/parishes/${parishId}/invites`, {
      method: 'POST',
      body: JSON.stringify({ email, role }),
    })
    invalidateCache(`/api/v1/parishes/${parishId}/invites`)
    return result
  },

  async revokeParishInvite(parishId, inviteId) {
    const result = await apiRequest(`/api/v1/parishes/${parishId}/invites/${inviteId}`, {
      method: 'DELETE',
    })
    invalidateCache(`/api/v1/parishes/${parishId}/invites`)
    return result
  },

  async resendParishInvite(parishId, inviteId) {
    return apiRequest(`/api/v1/parishes/${parishId}/invites/${inviteId}/resend`, {
      method: 'POST',
    })
  },

  async acceptInvite(token) {
    return apiRequest(`/api/v1/invites/accept`, {
      method: 'POST',
      body: JSON.stringify({ token }),
    })
  },

  async listParishMembers(parishId) {
    return apiRequest(`/api/v1/parishes/${parishId}/members`)
  },

  async updateParishMemberRole(parishId, userId, role) {
    const result = await apiRequest(`/api/v1/parishes/${parishId}/members/${userId}/role`, {
      method: 'PATCH',
      body: JSON.stringify({ role }),
    })
    invalidateCache(`/api/v1/parishes/${parishId}/members`)
    return result
  },

  // ============================================================================
  // Donation Fields
  // ============================================================================

  // Totals per dropdown field option, succeeded donations only. Takes the same
  // donationType the list takes — a total counting types the list is hiding
  // would overstate what the admin is looking at.
  async getParishDonationFieldSummary(parishId, { startDate, endDate, donationType } = {}) {
    const params = new URLSearchParams()
    if (startDate) params.append('startDate', startDate)
    if (endDate) params.append('endDate', endDate)
    if (donationType) params.append('donationType', donationType)
    return apiRequest(
      `/api/v1/parishes/${parishId}/donation-fields/summary${params.toString() ? `?${params.toString()}` : ''}`,
    )
  },

  async getParishDonationFields(parishId, { includeArchived = false } = {}) {
    return apiRequest(
      `/api/v1/parishes/${parishId}/donation-fields?includeArchived=${includeArchived}`,
    )
  },

  async createParishDonationField(parishId, body) {
    const result = await apiRequest(`/api/v1/parishes/${parishId}/donation-fields`, {
      method: 'POST',
      body: JSON.stringify(body),
    })
    invalidateCache(`/api/v1/parishes/${parishId}/donation-fields`)
    return result
  },

  async updateParishDonationField(parishId, fieldId, body) {
    const result = await apiRequest(`/api/v1/parishes/${parishId}/donation-fields/${fieldId}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    })
    invalidateCache(`/api/v1/parishes/${parishId}/donation-fields`)
    return result
  },

  async archiveParishDonationField(parishId, fieldId) {
    const result = await apiRequest(`/api/v1/parishes/${parishId}/donation-fields/${fieldId}`, {
      method: 'DELETE',
    })
    invalidateCache(`/api/v1/parishes/${parishId}/donation-fields`)
    return result
  },

  async restoreParishDonationField(parishId, fieldId) {
    const result = await apiRequest(
      `/api/v1/parishes/${parishId}/donation-fields/${fieldId}/restore`,
      { method: 'POST' },
    )
    invalidateCache(`/api/v1/parishes/${parishId}/donation-fields`)
    return result
  },

  async reorderParishDonationFields(parishId, orderedIds) {
    const result = await apiRequest(`/api/v1/parishes/${parishId}/donation-fields/reorder`, {
      method: 'PATCH',
      body: JSON.stringify({ orderedIds }),
    })
    invalidateCache(`/api/v1/parishes/${parishId}/donation-fields`)
    return result
  },

  // ============================================================================
  // Recurring Donations
  // ============================================================================

  // Create a recurring donation subscription
  // Body: { parishId, amountCents, interval, paymentMethodId (optional) }
  async createRecurringDonation({ parishId, amountCents, interval, paymentMethodId }) {
    const result = await apiRequest('/api/v1/parishes/recurring-donations', {
      method: 'POST',
      body: JSON.stringify({ parishId, amountCents, interval, paymentMethodId }),
    })
    invalidateCache('/api/v1/parishes/recurring-donations')
    return result
  },

  // Get all recurring donations for the current user
  async getUserRecurringDonations() {
    return cachedRequest('/api/v1/parishes/recurring-donations', 30_000)
  },

  // Get a single recurring donation by ID
  async getRecurringDonationById(id) {
    return cachedRequest(`/api/v1/parishes/recurring-donations/${id}`, 30_000)
  },

  // Update a recurring donation (amount or interval)
  // Body: { amountCents, interval }
  async updateRecurringDonation(id, { amountCents, interval }) {
    const result = await apiRequest(`/api/v1/parishes/recurring-donations/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ amountCents, interval }),
    })
    invalidateCache('/api/v1/parishes/recurring-donations')
    return result
  },

  // Cancel a recurring donation
  async cancelRecurringDonation(id) {
    const result = await apiRequest(`/api/v1/parishes/recurring-donations/${id}`, {
      method: 'DELETE',
    })
    invalidateCache('/api/v1/parishes/recurring-donations')
    return result
  },

  // Pause a recurring donation
  async pauseRecurringDonation(id) {
    const result = await apiRequest(`/api/v1/parishes/recurring-donations/${id}/pause`, {
      method: 'POST',
    })
    invalidateCache('/api/v1/parishes/recurring-donations')
    return result
  },

  // Resume a paused recurring donation
  async resumeRecurringDonation(id) {
    const result = await apiRequest(`/api/v1/parishes/recurring-donations/${id}/resume`, {
      method: 'POST',
    })
    invalidateCache('/api/v1/parishes/recurring-donations')
    return result
  },

  // Create a SetupIntent for collecting payment method
  async createSetupIntent(parishId) {
    return apiRequest('/api/v1/parishes/recurring-donations/setup-intent', {
      method: 'POST',
      body: JSON.stringify({ parishId }),
    })
  },

  // ============================================================================
  // Parish Announcements
  // ============================================================================

  // Get parish admins for host selection
  async getParishAdmins(parishId) {
    return cachedRequest(`/api/v1/parishes/${parishId}/events/admins`, 60_000)
  },

  // Get announcements for a parish (paginated)
  // Query: { page, limit }
  async getParishAnnouncements(parishId, { page = 1, limit = 20 } = {}) {
    const params = new URLSearchParams()
    if (page) params.append('page', page)
    if (limit) params.append('limit', limit)
    const query = params.toString()
    return cachedRequest(`/api/v1/parishes/${parishId}/announcements${query ? `?${query}` : ''}`, 30_000)
  },

  // Get a single announcement
  async getAnnouncement(parishId, announcementId) {
    return apiRequest(`/api/v1/parishes/${parishId}/announcements/${announcementId}`)
  },

  // Unified feed (parish + event + group announcements, merged and sorted) for one parish.
  // Not cached: items carry a per-viewer isUnread flag that changes after markAnnouncementsRead.
  async getParishAnnouncementsFeed(parishId, { page = 1, limit = 3 } = {}) {
    const params = new URLSearchParams()
    if (page) params.append('page', page)
    if (limit) params.append('limit', limit)
    return apiRequest(`/api/v1/parishes/${parishId}/announcements/feed?${params.toString()}`)
  },

  // Unified feed across every parish/group the current user belongs to, for the header bell.
  async getMyAnnouncementsFeed({ page = 1, limit = 10 } = {}) {
    const params = new URLSearchParams()
    if (page) params.append('page', page)
    if (limit) params.append('limit', limit)
    return apiRequest(`/api/v1/announcements/feed?${params.toString()}`)
  },

  // Mark announcements as read. Body: { items: [{ type: 'parish'|'event'|'group', id }] }
  async markAnnouncementsRead(items) {
    return apiRequest('/api/v1/announcements/mark-read', {
      method: 'POST',
      body: JSON.stringify({ items }),
    })
  },

  // Create a new announcement
  // Body: { title, content, publishedAt? }  (omit publishedAt to post now; future = scheduled)
  async createAnnouncement(parishId, data) {
    const result = await apiRequest(`/api/v1/parishes/${parishId}/announcements`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
    invalidateCache(`/api/v1/parishes/${parishId}/announcements`)
    return result
  },

  // Update an announcement (partial update)
  async updateAnnouncement(parishId, announcementId, data) {
    const result = await apiRequest(`/api/v1/parishes/${parishId}/announcements/${announcementId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
    invalidateCache(`/api/v1/parishes/${parishId}/announcements`)
    return result
  },

  // Delete an announcement
  async deleteAnnouncement(parishId, announcementId) {
    const result = await apiRequest(`/api/v1/parishes/${parishId}/announcements/${announcementId}`, {
      method: 'DELETE',
    })
    invalidateCache(`/api/v1/parishes/${parishId}/announcements`)
    return result
  },

  // ============================================================================
  // Parish Groups (restricted groups — see groups.pdf)
  // ============================================================================
  // Groups are hidden from non-members: the API returns 404 (never 403) for a
  // group the caller isn't in, and list endpoints already filter server-side.
  // All list endpoints return bare arrays, not a { data, meta } envelope.

  // Groups the caller can see for a parish (admins see all of their parish's)
  async getParishGroups(parishId) {
    return cachedRequest(`/api/v1/parishes/${parishId}/groups`, 30_000)
  },

  // Create a group. Body: { name, description? }
  async createGroup(parishId, data) {
    const result = await apiRequest(`/api/v1/parishes/${parishId}/groups`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
    invalidateCache(`/api/v1/parishes/${parishId}/groups`)
    return result
  },

  // Search users to add as members. scope 'followers' (default) matches parish
  // followers by name/email substring; 'all' matches any user by name substring
  // or exact email. q must be at least 2 characters. Returns up to 20 users.
  // Not cached — search results change with every keystroke.
  async searchGroupMemberCandidates(parishId, { q, scope = 'followers' } = {}) {
    const params = new URLSearchParams()
    params.append('q', q)
    params.append('scope', scope)
    return apiRequest(`/api/v1/parishes/${parishId}/groups/member-search?${params.toString()}`)
  },

  // Get a single group (404 if the caller isn't a member or the owning admin)
  async getGroup(groupId) {
    return apiRequest(`/api/v1/groups/${groupId}`)
  },

  // List group members — each row includes user: { id, name, email }
  async getGroupMembers(groupId) {
    return apiRequest(`/api/v1/groups/${groupId}/members`)
  },

  // Add an existing app user to the group. Throws 409 if already a member.
  async addGroupMember(groupId, userId) {
    const result = await apiRequest(`/api/v1/groups/${groupId}/members`, {
      method: 'POST',
      body: JSON.stringify({ userId }),
    })
    invalidateCache(`/api/v1/groups/${groupId}/members`)
    return result
  },

  // Remove a member (204 No Content)
  async removeGroupMember(groupId, userId) {
    const result = await apiRequest(`/api/v1/groups/${groupId}/members/${userId}`, {
      method: 'DELETE',
    })
    invalidateCache(`/api/v1/groups/${groupId}/members`)
    return result
  },

  // List a group's announcements (admins also see drafts)
  async getGroupAnnouncements(groupId) {
    return apiRequest(`/api/v1/groups/${groupId}/announcements`)
  },

  // Create a group announcement. Body: { title, content, isPublished?, publishedAt? }
  // Publishing fans out push + email to every member.
  async createGroupAnnouncement(groupId, data) {
    const result = await apiRequest(`/api/v1/groups/${groupId}/announcements`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
    invalidateCache(`/api/v1/groups/${groupId}/announcements`)
    return result
  },

  // Update a group announcement (partial). Body: { title?, content?, isPublished? }
  // Editing an already-published announcement does NOT re-notify members.
  async updateGroupAnnouncement(groupId, announcementId, data) {
    const result = await apiRequest(
      `/api/v1/groups/${groupId}/announcements/${announcementId}`,
      {
        method: 'PATCH',
        body: JSON.stringify(data),
      },
    )
    invalidateCache(`/api/v1/groups/${groupId}/announcements`)
    return result
  },

  // Delete a group announcement (soft delete server-side)
  async deleteGroupAnnouncement(groupId, announcementId) {
    const result = await apiRequest(
      `/api/v1/groups/${groupId}/announcements/${announcementId}`,
      { method: 'DELETE' },
    )
    invalidateCache(`/api/v1/groups/${groupId}/announcements`)
    return result
  },

  // ============================================================================
  // Financial Connections / Round-ups
  // ============================================================================

  // Create a Financial Connections session for linking a bank
  async createFinancialConnectionsSession() {
    return apiRequest('/api/v1/financial-connections/create-session', { method: 'POST' })
  },

  // Persist accounts linked in a completed FC session
  async linkFinancialConnectionsAccounts(sessionId) {
    const result = await apiRequest('/api/v1/financial-connections/link-accounts', {
      method: 'POST',
      body: JSON.stringify({ sessionId }),
    })
    invalidateCache('/api/v1/financial-connections/banks', '/api/v1/financial-connections/transactions')
    return result
  },

  // Manually trigger a transaction refresh + sync for all linked banks
  async syncBankTransactions() {
    const result = await apiRequest('/api/v1/financial-connections/sync', { method: 'POST' })
    invalidateCache('/api/v1/financial-connections/transactions')
    return result
  },

  // Get stored transactions in the next-donation window
  async getBankTransactions() {
    return cachedRequest('/api/v1/financial-connections/transactions', 30_000)
  },

  // Get linked banks
  async getLinkedBanks() {
    return cachedRequest('/api/v1/financial-connections/banks', 60_000)
  },

  // Unlink a bank account
  async unlinkBank(id) {
    const result = await apiRequest(`/api/v1/financial-connections/banks/${id}`, { method: 'DELETE' })
    invalidateCache('/api/v1/financial-connections/banks', '/api/v1/financial-connections/transactions')
    return result
  },

  // Get roundup settings (enabled, parish, payment method status)
  async getRoundupSettings() {
    return cachedRequest('/api/v1/financial-connections/roundup/settings', 60_000)
  },

  // Update roundup settings (enable/disable, set parish, boost factor)
  async updateRoundupSettings({ roundupsEnabled, roundupParishId, roundupBoostFactor }) {
    const result = await apiRequest('/api/v1/financial-connections/roundup/settings', {
      method: 'POST',
      body: JSON.stringify({ roundupsEnabled, roundupParishId, roundupBoostFactor }),
    })
    invalidateCache('/api/v1/financial-connections/roundup/settings')
    return result
  },

  // Create SetupIntent for saving a roundup payment method
  async createRoundupSetupIntent() {
    return apiRequest('/api/v1/financial-connections/roundup/setup-intent', { method: 'POST' })
  },

  // Save payment method after SetupIntent completes
  async saveRoundupPaymentMethod(setupIntentId) {
    const result = await apiRequest('/api/v1/financial-connections/roundup/save-payment-method', {
      method: 'POST',
      body: JSON.stringify({ setupIntentId }),
    })
    invalidateCache('/api/v1/financial-connections/roundup/settings')
    return result
  },

  // ============================================================================
  // Notification Preferences
  // ============================================================================

  async getNotificationPreferences() {
    return cachedRequest('/api/v1/notification-preferences', 5 * 60 * 1000)
  },

  async updateNotificationPreferences(data) {
    const result = await apiRequest('/api/v1/notification-preferences', {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
    invalidateCache('/api/v1/notification-preferences')
    return result
  },

  async sendTestNotificationEmail() {
    return apiRequest('/api/v1/notification-preferences/test', {
      method: 'POST',
    })
  },

  async sendTestNotificationEmailToUser(userId) {
    return apiRequest(`/api/v1/notification-preferences/test/${userId}`, {
      method: 'POST',
    })
  },

  // Parish update change proposals
  async getChangeProposals(parishId, batchId) {
    return apiRequest(`/api/v1/parish-update/proposals?parishId=${parishId}&batchId=${batchId}`)
  },

  // payload (optional): the reviewer's edited version of the proposal's data.
  // When provided, the backend persists and applies it instead of the stored payload.
  async approveProposal(proposalId, payload) {
    return apiRequest(`/api/v1/parish-update/proposals/${proposalId}/approve`, {
      method: 'POST',
      ...(payload ? { body: JSON.stringify({ payload }) } : {}),
    })
  },

  async rejectProposal(proposalId) {
    return apiRequest(`/api/v1/parish-update/proposals/${proposalId}/reject`, { method: 'POST' })
  },
}

export async function adminListReports({ status = 'pending' } = {}) {
  return apiRequest(`/api/v1/admin/reports?status=${status}`)
}

export async function adminCountPendingReports() {
  return apiRequest(`/api/v1/admin/reports?count=true`)
}

export async function adminActOnReport(id, body) {
  return apiRequest(`/api/v1/admin/reports/${id}`, { method: 'PATCH', body: JSON.stringify(body) })
}

export async function adminListRefundRequests({ status } = {}) {
  const qs = status && status !== 'all' ? `?status=${encodeURIComponent(status)}` : ''
  return apiRequest(`/api/v1/admin/refund-requests${qs}`)
}

export async function adminApproveRefundRequest(id, note) {
  return apiRequest(`/api/v1/admin/refund-requests/${id}/approve`, {
    method: 'POST',
    body: JSON.stringify(note && note.trim() ? { note: note.trim() } : {}),
  })
}

export async function adminRejectRefundRequest(id, note) {
  return apiRequest(`/api/v1/admin/refund-requests/${id}/reject`, {
    method: 'POST',
    body: JSON.stringify({ note }),
  })
}

export async function adminListDisputes({ status = 'open' } = {}) {
  return apiRequest(`/api/v1/admin/disputes?status=${status}`)
}

export const publicApi = {
  async searchParishes({ page = 1, limit = 20, search, state } = {}) {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) })
    if (search) params.append('search', search)
    if (state) params.append('state', state)
    return publicRequest(`/api/v1/public/parishes?${params.toString()}`)
  },

  async getParish(parishId) {
    return publicRequest(`/api/v1/public/parishes/${parishId}`)
  },

  async getFeeSchedule() {
    return publicRequest('/api/v1/public/fees')
  },

  async getParishEvents(parishId, { page = 1, limit = 20 } = {}) {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) })
    return publicRequest(`/api/v1/public/parishes/${parishId}/events?${params.toString()}`)
  },

  async getParishEvent(parishId, eventId) {
    return publicRequest(`/api/v1/public/parishes/${parishId}/events/${eventId}`)
  },

  async createGuestCheckoutSession({ parishId, amount, returnUrl }) {
    return publicRequest(`/api/v1/public/parishes/${parishId}/checkout-session`, {
      method: 'POST',
      body: JSON.stringify({ amount, returnUrl }),
    })
  },

  async verifyGuestDonation(sessionId) {
    return publicRequest(
      `/api/v1/public/checkout-session/verify?session_id=${encodeURIComponent(sessionId)}`,
      { method: 'POST' },
    )
  },

  async redeemWebHandoff(token) {
    return publicRequest('/api/v1/giving/web-handoff/redeem', {
      method: 'POST',
      body: JSON.stringify({ token }),
    })
  },
}
